import type {
  AlertChannel,
  AlertKind,
  AlertRuleDto,
  CreateAlertRuleRequest,
  UpdateAlertRuleRequest
} from '@/lib/dto'

/**
 * Модель формы правила алерта: подписи, значения по умолчанию, валидация и
 * сборка тела запроса.
 *
 * Почему это отдельный модуль, а не «всё в диалоге». Здесь живут два правила,
 * нарушение которых не видно глазами и стоит дорого:
 *
 *  1. `targetMasked` НИКОГДА не уходит обратно на сервер. Маска (`•••7890`) —
 *     не адрес; отправив её, панель молча превратила бы рабочее правило в
 *     правило, которое пишет в несуществующий чат. Поэтому «адрес» в теле
 *     запроса появляется только тогда, когда пользователь его действительно
 *     ввёл, — за это отвечает флаг `targetEditing`, а не «поле не пустое»
 *     (у Telegram пустая строка — законное значение «чат по умолчанию»).
 *  2. Смена канала обязана идти вместе с новым адресом — иначе сервер отвечает
 *     409 (docs/06 §7.1). Форма не даёт этого не заметить: смена канала при
 *     правке принудительно открывает поле адреса.
 *
 * Ключи ошибок совпадают с `errors[].path` сервера (`config.minutes`,
 * `config.pattern`, `target`), поэтому серверная валидация подсвечивает то же
 * самое поле, что и клиентская, без таблицы соответствий.
 */

export interface AlertKindMeta {
  label: string
  /** Одна строка: когда правило сработает. Читается под селектом. */
  when: string
}

export const ALERT_KIND_META: Record<AlertKind, AlertKindMeta> = {
  botDown: {
    label: 'Бот не в игре',
    when: 'Бота просили работать, а он не в игре дольше заданного времени'
  },
  authFailed: {
    label: 'Проблема с авторизацией',
    when: 'Minecraft-аккаунт разлогинился или бот не смог войти'
  },
  kickMatch: {
    label: 'Кик по шаблону',
    when: 'Сообщение о кике за последние 5 минут подошло под регулярное выражение'
  },
  degraded: {
    label: 'Работа с ограничениями',
    when: 'Бот перешёл в DEGRADED: часть пакетов сервера ядро не разбирает'
  }
}

export const ALERT_CHANNEL_META: Record<AlertChannel, { label: string; targetLabel: string }> = {
  telegram: { label: 'Telegram', targetLabel: 'Chat ID или @канал' },
  webhook: { label: 'Webhook', targetLabel: 'URL webhook' }
}

/** Границы `botDown.minutes` — те же, что у `BotDownConfigSchema` на сервере. */
export const MIN_BOT_DOWN_MINUTES = 1
export const MAX_BOT_DOWN_MINUTES = 24 * 60
/** Предел длины regex — `MAX_PATTERN_LENGTH` сервера. */
export const MAX_PATTERN_LENGTH = 200

/** `-1001234567890`, `123456789` или `@channel_name` — копия серверной проверки. */
const TELEGRAM_CHAT_ID = /^(-?\d{1,20}|@[A-Za-z][A-Za-z0-9_]{4,63})$/

export interface AlertFormState {
  name: string
  kind: AlertKind
  channel: AlertChannel
  /** Введённый пользователем адрес. Осмыслен только при `targetEditing`. */
  target: string
  /**
   * Открыто ли поле адреса. При создании — всегда; при правке — только когда
   * пользователь сам нажал «Изменить адрес» или сменил канал.
   */
  targetEditing: boolean
  /** `botDown`: порог в минутах. Строка, а не число: пустой инпут — не 0. */
  minutes: string
  /** `kickMatch`: регулярное выражение и его флаги. */
  pattern: string
  flags: string
  enabled: boolean
}

export function emptyAlertForm(): AlertFormState {
  return {
    name: '',
    kind: 'botDown',
    channel: 'telegram',
    target: '',
    targetEditing: true,
    minutes: '5',
    pattern: '',
    flags: 'i',
    enabled: true
  }
}

/** Форма из существующего правила. Адрес закрыт: наружу отдана только маска. */
export function alertFormOf(rule: AlertRuleDto): AlertFormState {
  const minutes = rule.config['minutes']
  const pattern = rule.config['pattern']
  const flags = rule.config['flags']
  return {
    name: rule.name,
    kind: rule.kind,
    channel: rule.channel,
    target: '',
    targetEditing: false,
    minutes: typeof minutes === 'number' ? String(minutes) : '5',
    pattern: typeof pattern === 'string' ? pattern : '',
    flags: typeof flags === 'string' ? flags : 'i',
    enabled: rule.enabled
  }
}

/** Есть ли у вида правила собственные настройки — от этого зависит вёрстка. */
export function kindHasConfig(kind: AlertKind): boolean {
  return kind === 'botDown' || kind === 'kickMatch'
}

/**
 * Проверка формы. Возвращает `{ путь: сообщение }` — пусто, значит можно
 * отправлять. Дублирует серверные ограничения намеренно: ждать 400, чтобы
 * узнать «минуты — от 1 до 1440», это лишний круг по сети на каждой опечатке.
 */
export function validateAlertForm(form: AlertFormState): Record<string, string> {
  const found: Record<string, string> = {}

  const name = form.name.trim()
  if (name === '') found.name = 'Название нужно, чтобы отличать правила в списке'
  else if (name.length > 64) found.name = 'Не длиннее 64 символов'

  if (form.kind === 'botDown') {
    const minutes = Number(form.minutes)
    if (
      form.minutes.trim() === '' ||
      !Number.isInteger(minutes) ||
      minutes < MIN_BOT_DOWN_MINUTES ||
      minutes > MAX_BOT_DOWN_MINUTES
    ) {
      found['config.minutes'] =
        `Целое число от ${MIN_BOT_DOWN_MINUTES} до ${MAX_BOT_DOWN_MINUTES} (сутки)`
    }
  }

  if (form.kind === 'kickMatch') {
    const pattern = form.pattern.trim()
    if (pattern === '') found['config.pattern'] = 'Без шаблона правило не сработает никогда'
    else if (pattern.length > MAX_PATTERN_LENGTH) {
      found['config.pattern'] = `Не длиннее ${MAX_PATTERN_LENGTH} символов`
    } else if (!/^[imsu]*$/.test(form.flags.trim())) {
      found['config.pattern'] = 'Допустимые флаги: i, m, s, u'
    }
  }

  // Адрес проверяем только когда он вообще редактируется: при правке имени
  // сервер сохранит прежний, и требовать ввести его заново нельзя — панель его
  // не знает.
  if (form.targetEditing) {
    const target = form.target.trim()
    if (form.channel === 'telegram') {
      if (target !== '' && !TELEGRAM_CHAT_ID.test(target)) {
        found.target = 'Числовой chat id (например -1001234567890) или @имя_канала'
      }
    } else if (target === '') {
      found.target = 'Для webhook нужен адрес'
    } else if (!/^https?:\/\/\S+$/i.test(target)) {
      found.target = 'Абсолютный URL с http:// или https://'
    }
  }

  return found
}

/** `config` под выбранный вид. Лишних полей от прошлого вида не остаётся. */
export function alertConfigOf(form: AlertFormState): Record<string, unknown> {
  switch (form.kind) {
    case 'botDown':
      return { minutes: Number(form.minutes) }
    case 'kickMatch': {
      const flags = form.flags.trim()
      return { pattern: form.pattern.trim(), ...(flags === '' ? {} : { flags }) }
    }
    case 'authFailed':
    case 'degraded':
      return {}
  }
}

export function createRequestOf(form: AlertFormState): CreateAlertRuleRequest {
  return {
    name: form.name.trim(),
    kind: form.kind,
    channel: form.channel,
    target: form.target.trim(),
    config: alertConfigOf(form),
    enabled: form.enabled
  }
}

/**
 * Тело PATCH. Отправляем только изменённое — по двум причинам:
 * сервер пишет в аудит список изменённых полей (лишние поля = ложь в аудите),
 * и `target` обязан отсутствовать, если пользователь его не трогал.
 */
export function updateRequestOf(
  form: AlertFormState,
  original: AlertRuleDto
): UpdateAlertRuleRequest {
  const dto: UpdateAlertRuleRequest = {}
  const name = form.name.trim()
  if (name !== original.name) dto.name = name
  if (form.kind !== original.kind) dto.kind = form.kind
  if (form.channel !== original.channel) dto.channel = form.channel
  if (form.enabled !== original.enabled) dto.enabled = form.enabled

  const config = alertConfigOf(form)
  // Вид сменился — конфиг обязан уехать целиком, даже если совпал по строке:
  // сервер перепроверяет его под новый вид.
  if (form.kind !== original.kind || !sameConfig(config, original.config)) dto.config = config

  // Единственное место, где адрес попадает в запрос. Флага нет — ключа нет,
  // и сервер оставляет прежний зашифрованный адрес нетронутым.
  if (form.targetEditing) dto.target = form.target.trim()

  return dto
}

/** Поверхностное сравнение `config`: значения там только строки и числа. */
function sameConfig(next: Record<string, unknown>, previous: Record<string, unknown>): boolean {
  const nextKeys = Object.keys(next)
  const previousKeys = Object.keys(previous)
  if (nextKeys.length !== previousKeys.length) return false
  return nextKeys.every(key => next[key] === previous[key])
}
